import React, {useState} from "react";
import {Product} from "depoto-core";
import {Modal} from "./Modal";
import {useCore} from "../hooks";
import {formatPrice, Schema} from "../utils";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {ImagePreview} from "./ImagePreview";
import './ProductStockColumn.css'
import './ModalStockOverview.css'

type StockDepotGroup = {
  depotId: number
  depotName: string
  sumStock: number
  sumReservation: number
  sumAvailable: number
  sumPurchase: number
  productDepots: any[]
}

type Props = {
  product: Product
  quantityStock: number
  quantityReservation: number
  quantityAvailable: number
}

export const ModalStockOverview: React.FC<Props> = ({ product, quantityStock, quantityReservation, quantityAvailable }) => {
  const { core, currency } = useCore()
  const [isVisible, setIsVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [stockGroups, setStockGroups] = useState<StockDepotGroup[]>([])
  const [totalPurchase, setTotalPurchase] = useState(0)

  const openModal = async () => {
    setIsVisible(true)
    setIsLoading(true)
    try {
      const p = await core.services.product.getById(product.id, Schema.product.stockOverview)
      if (p) {
        const depotMap = new Map<number, StockDepotGroup>()

        for (const q of (p.quantities || [])) {
          const depotId = Number(q.depot?.id)
          if (!depotId) continue
          depotMap.set(depotId, {
            depotId,
            depotName: q.depot?.name || `Sklad #${depotId}`,
            sumStock: Number(q.quantityStock || 0),
            sumReservation: Number(q.quantityReservation || 0),
            sumAvailable: Number(q.quantityAvailable || 0),
            sumPurchase: Number(q.quantityPurchase || 0),
            productDepots: [],
          })
        }

        for (const pd of (p.productDepots || [])) {
          const depotId = Number(pd.depot?.id)
          if (!depotId) continue
          if (pd.quantityStock <= 0 && pd.quantityReservation <= 0) continue
          let group = depotMap.get(depotId)
          if (!group) {
            group = {
              depotId,
              depotName: pd.depot?.name || `Sklad #${depotId}`,
              sumStock: 0, sumReservation: 0, sumAvailable: 0, sumPurchase: 0,
              productDepots: [],
            }
            depotMap.set(depotId, group)
          }
          group.productDepots.push(pd)
        }

        const allGroups = Array.from(depotMap.values())
          .filter(g => g.sumStock > 0 || g.sumReservation > 0 || g.sumPurchase > 0 || g.productDepots.length > 0)
        setStockGroups(allGroups)
        setTotalPurchase(allGroups.reduce((s, g) => s + g.sumPurchase, 0))
      }
    } catch (e) {
      console.warn('Failed to load stock overview:', e)
    } finally {
      setIsLoading(false)
    }
  }

  const closeModal = () => {
    setIsVisible(false)
    setStockGroups([])
  }

  return (
    <Modal
      modalOpenBtn={
        <div className="flex flex-row justify-end cursor-pointer" onClick={openModal}>
          <div className="stock-label quantity-stock bg-primary mr-1">{quantityStock}</div>
          <div className="stock-label quantity-reservation bg-error mr-1">{quantityReservation}</div>
          <div className="stock-label quantity-available bg-success">{quantityAvailable}</div>
        </div>
      }
      isVisible={isVisible}
      onClose={closeModal}
      modalTitle={'Skladové zásoby'}
      modalWidth={'xxl'}
      autoHeight={true}
      contentClassName="so-modal-content"
      headerBtn={
        <button className="text-gray-700 hover:text-gray-900 text-lg p-1" onClick={closeModal}>
          <FontAwesomeIcon icon="times" />
        </button>
      }
      content={
        <div>
          {/* Product info */}
          <div className="flex items-center gap-3 pb-4 mb-4 border-b border-gray-200">
            <ImagePreview image={product.mainImage} />
            <div className="flex flex-col">
              <div className="font-semibold">{product.fullName}</div>
              <div className="text-sm text-gray-500 mt-0.5">
                {product.ean ? <span>EAN: {product.ean}</span> : null}
                {product.code && product.ean ? <span>&nbsp;&nbsp;</span> : null}
                {product.code ? <span>Code: {product.code}</span> : null}
              </div>
            </div>
          </div>

          {/* Summary cards */}
          <div className="flex gap-3 mb-4">
            <SummaryCard icon={['far', 'warehouse-alt']} label="Celkem skladem" value={Number(product.quantityStock || 0)} circleBg="bg-blue-100" circleColor="text-blue-600" />
            <SummaryCard icon={['far', 'hourglass']} label="Celkem rezervace" value={Number(product.quantityReservation || 0)} circleBg="bg-red-100" circleColor="text-red-600" />
            <SummaryCard icon={['far', 'check']} label="Celkem dostupné" value={Number(product.quantityAvailable || 0)} circleBg="bg-green-100" circleColor="text-green-600" />
            <SummaryCard icon={['far', 'truck']} label="Zboží na cestě" value={totalPurchase} circleBg="bg-orange-100" circleColor="text-orange-600" />
          </div>

          {/* Depot sections */}
          {isLoading ? (
            <div className="text-center py-6 text-gray-400 text-sm">Načítání...</div>
          ) : (
            stockGroups.map(group => (
              <div key={group.depotId} className="border border-gray-200 rounded-lg p-3 mb-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-semibold text-sm">Sklad {group.depotName}</div>
                  <div className="flex justify-around items-center gap-3 bg-gray-50 border border-gray-200 rounded-md px-5 py-3 text-sm">
                    <span className="flex items-center gap-1.5 text-blue-600"><FontAwesomeIcon icon={['far', 'warehouse-alt']} /> {group.sumStock}</span>
                    <span className="flex items-center gap-1.5 text-red-500"><FontAwesomeIcon icon={['far', 'hourglass']} /> {group.sumReservation}</span>
                    <span className="flex items-center gap-1.5 text-green-500"><FontAwesomeIcon icon={['far', 'check']} /> {group.sumAvailable}</span>
                    {group.sumPurchase > 0 ? (
                      <span className="flex items-center gap-1.5 text-orange-500"><FontAwesomeIcon icon={['far', 'truck']} /> {group.sumPurchase}</span>
                    ) : null}
                  </div>
                </div>
                {group.productDepots.length > 0 ? (
                  <table className="so-table">
                    <thead>
                      <tr>
                        <th className="text-left">Dodavatel</th>
                        <th className="text-left">Šarže</th>
                        <th className="text-left">Expirace</th>
                        <th className="text-left">Pozice</th>
                        <th className="text-center">Dostupnost</th>
                        <th className="text-right">Nákupní cena</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.productDepots.map((pd: any) => (
                        <tr key={pd.id}>
                          <td>{pd.supplier?.name || ''}</td>
                          <td>{pd.batch || ''}</td>
                          <td>{pd.expirationDate ? new Date(pd.expirationDate).toLocaleDateString('cs-CZ') : ''}</td>
                          <td>{pd.position || ''}</td>
                          <td className="text-center whitespace-nowrap">
                            <div className="flex flex-row items-center justify-center">
                              <span className="stock-label bg-primary mr-0.5">{pd.quantityStock}</span>
                              <span className="stock-label bg-error mr-0.5">{pd.quantityReservation}</span>
                              <span className="stock-label bg-success">{Math.max(0, pd.quantityStock - pd.quantityReservation)}</span>
                            </div>
                          </td>
                          <td className="text-right whitespace-nowrap">{formatPrice(pd.purchasePrice, currency.id)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : null}
              </div>
            ))
          )}
          {!isLoading && stockGroups.length === 0 ? (
            <div className="text-center py-6 text-gray-400 text-sm">Žádná data</div>
          ) : null}
        </div>
      }
    />
  )
}

function SummaryCard({ icon, label, value, circleBg, circleColor }: {
  icon: any; label: string; value: number; circleBg: string; circleColor: string
}) {
  return (
    <div className="flex-1 flex items-center justify-between p-3.5 rounded-lg bg-gray-50 border border-gray-200">
      <div className="flex flex-col">
        <div className="text-3xl font-bold leading-tight">{value}</div>
        <div className="text-xs text-gray-500 mt-0.5">{label}</div>
      </div>
      <div className={`so-icon-circle ${circleBg} ${circleColor}`}>
        <FontAwesomeIcon icon={icon as any} />
      </div>
    </div>
  )
}
